import React, { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import useTeeGeeIdentity from './useTeeGeeIdentity';

// Lazy so each bot's code is only fetched on pages that actually show it, and
// so a visitor never downloads the authenticated companion at all.
const TsgAiBot = lazy(() => import('./TsgAiBot'));
const TeeGeeCompanion = lazy(() => import('./TeeGeeCompanion'));

/**
 * Chooses WHICH TeeGee to render, and — more importantly — guarantees that one
 * student's TeeGee can never be shown to another.
 *
 * ─── THE KEY IS THE FIX ──────────────────────────────────────────────────
 * The subtree is keyed on the session identity. When that changes — logout,
 * login, account switch, even a re-login to the same account — React unmounts
 * the previous tree and mounts a fresh one. Every piece of component state
 * inside is destroyed by React itself.
 *
 * This is the structural answer to the original bug. TeeGee's messages lived
 * in useState inside a component mounted at the app root that nothing ever
 * unmounted, so clearing localStorage could not touch them: they were never in
 * localStorage. Handing the problem to React's reconciler means correctness no
 * longer depends on every future contributor remembering to write a reset
 * handler — the state cannot outlive the session because the component cannot.
 *
 * ─── GUEST vs AUTHENTICATED (brief sections 4 and 5) ─────────────────────
 * Two genuinely different components, not one component with a flag. The guest
 * bot has no code path that can request private data, and the companion has no
 * code path that runs without a session token. The separation is structural,
 * so a bug in one cannot become a data leak in the other.
 *
 * The identity read here is a CLIENT-SIDE HINT used only for this choice. The
 * server independently derives the real owner from the session token, so a
 * tampered localStorage can at worst show an empty companion panel.
 */

/**
 * TeeGee shows broadly — home, login, register, student and builder dashboards
 * and most product pages. It is hidden only where a floating chat clearly does
 * not belong:
 *   * the back-office admin / super-admin / CRM consoles, and
 *   * the FULL-SCREEN in-app chat surfaces (the AI Tutor and TSG notes), where
 *     a second floating chat would collide with the existing one.
 *
 * Deny-list, so any new page gets the bot automatically.
 */
const HIDDEN_PREFIXES = [
  '/superadmin',
  '/admin-console',
  '/admin',                       // admin back-office (incl. /admin/login)
  '/nextgen-builder/ai-tutor',    // full-screen AI Tutor chat
  '/student/tsg',                 // TSG notes / tutor surface
  '/tsg',                         // learner-side AI Tutor surface
  '/chatbot',                     // legacy student tutor route
];

const isHidden = (pathname) =>
  HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

export default function TsgAiBotGate() {
  const { pathname } = useLocation();
  const identity = useTeeGeeIdentity();

  if (isHidden(pathname)) return null;

  return (
    <Suspense fallback={null}>
      {/* The key is load-bearing. Do not remove it or hoist either bot above
          it — that would restore the exact cross-user state bug this fixes. */}
      <div key={identity.identityKey} style={{ display: 'contents' }}>
        {identity.authed ? <TeeGeeCompanion /> : <TsgAiBot />}
      </div>
    </Suspense>
  );
}

export { isHidden, HIDDEN_PREFIXES };
